import { type Observable } from 'rxjs'
import { type GetCurrentMemberFn } from '@/features/iam'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import {
  type SnackbarFactory,
  type SnackbarRetryWrapperFactory,
} from '@/features/shell'
import { type TransactionsScope } from '@/features/billing/transactions/TransactionStore'
import { type TransactionsTableViewModel } from '@/features/billing/transactions/TransactionsTable/TransactionsTableViewModel'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import type { NewBillingMethodDialogViewModel } from './profile/NewBillingMethodDialog/NewBillingMethodDialogViewModel'
import type { WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import type { CardBillingMethodEditorViewModel } from '@/features/billing/profile/CardBillingMethodEditor/CardBillingMethodEditorViewModel'
import type { BillingMethodListViewModel } from '@/features/billing/profile/BillingMethodList/BillingMethodListViewModel'
import type { Coupon } from '@/features/billing/coupons/Coupon'
import type { ApplePayViewModel } from '@/features/billing/profile/ApplePayViewModel'

/**
 * Function for checking if a given member has at least one billing method.
 */
export type CheckHasBillingMethodFn = (
  member: WorkspaceMember,
) => Promise<boolean>

/**
 * Options and dependencies of the billing feature.
 */
export interface BillingFeatureOptions {
  token$: Observable<string>
  getCurrentMember: GetCurrentMemberFn
  newSnackbar: SnackbarFactory
  createSnackbarRetryWrapper: SnackbarRetryWrapperFactory
}

/**
 * Function for changing the default billing method.
 */
export type ChangeDefaultBillingMethodFn = (
  method: BillingMethod,
) => Promise<void>

/**
 * Factory for creating a new billing method dialog view model based
 * on the specified scope.
 */
export type NewBillingMethodDialogViewModelFactory =
  () => NewBillingMethodDialogViewModel

/**
 * Factory for creating a card billing method editor view model.
 */
export type CardBillingMethodEditorViewModelFactory =
  () => CardBillingMethodEditorViewModel

/**
 * Factory for creating a billing method list view model.
 */
export type BillingMethodListViewModelFactory = () => BillingMethodListViewModel

/**
 * Factory for creating an Apple Pay view model.
 */
export type ApplePayViewModelFactory = (
  amount$: Observable<number>,
  submit: () => Promise<void>,
) => ApplePayViewModel

/**
 * Factory for creating a transactions table view model based
 * on the specified scope.
 */
export type TransactionsTableViewModelFactory = (
  scope: TransactionsScope,
) => TransactionsTableViewModel

/**
 * Function for listing the current user's coupons.
 */
export type ListCouponsFn = (signal?: AbortSignal) => Promise<Coupon[]>

/**
 * Function for adding a coupon to the current user's profile.
 */
export type AddCouponFn = (code: string) => Promise<Coupon>

/**
 * Function for fetching a coupon by ID.
 */
export type FetchCouponFn = (
  id: string,
  signal?: AbortSignal,
) => Promise<Coupon>

/**
 * Billing-related features.
 */
export type BillingFeature = ReturnType<typeof BillingFeature>

export function BillingFeature(options: BillingFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (module) => module.InternalBillingFeature(options),
  )

  return {
    // Internal
    internal,

    // Public
    changeDefaultBillingMethod: internal.wrap(
      (m) => m.changeDefaultBillingMethod,
    ) as ChangeDefaultBillingMethodFn,
    createBillingMethodListViewModel: internal.lazy(
      (m) => m.createBillingMethodListViewModel,
    ),
    createCardBillingMethodEditorViewModel: internal.lazy(
      (m) => m.createCardBillingMethodEditorViewModel,
    ),
    createTransactionsTableViewModel: internal.lazy(
      (m) => m.createTransactionsTableViewModel,
    ),
    createNewBillingMethodDialogViewModel: internal.lazy(
      (m) => m.createNewBillingMethodDialogViewModel,
    ),
    createApplePayViewModel: internal.lazy((m) => m.createApplePayViewModel),
    checkHasDefaultBillingMethod: internal.wrap(
      (m) => m.checkHasDefaultBillingMethod,
    ) as CheckHasBillingMethodFn,
    listCoupons: internal.wrap((m) => m.listCoupons) as ListCouponsFn,
    addCoupon: internal.wrap((m) => m.addCoupon) as AddCouponFn,
    fetchCoupon: internal.wrap((m) => m.fetchCoupon) as FetchCouponFn,
  }
}
